import * as React from 'react';
import { flow } from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { IStore } from '../../../../redux/IStore';
import NavigationBar from '../../components/NavigationBar';
import {Form, FormGroup, Col} from 'react-bootstrap';
import { actions as quoteActions } from '../../../../services/quote/reducer';
import { formValueSelector, InjectedFormProps, reduxForm} from 'redux-form';
import {IChannel, IIAS, IIOBSP, IProduct} from '../../../../data/api/models';
import {IAuthState} from '../../../../services/auth/models';
import {II18nMessages} from '../../../../services/branding/models';
import {IQuoteActions, IQuoteState} from '../../../../services/quote/models';
import {hasSameValue} from '../../../../util/validators';
import DeclarationQuestions from '../../components/DeclarationQuestions';
import DeclinesAndReferrals from '../../components/DeclinesAndReferrals';
import {getReferralsAndDeclines} from '../../../../util/helpers';
import {Email} from '../../../../components/Email';
import {CheckboxGroup} from '../../../../components/CheckboxGroup';

interface IIOBSPFormProps extends InjectedFormProps {
  dispatch: any;
  auth: IAuthState;
  startDate: string;
  product: IProduct;
  renewal: boolean;
  channel: IChannel;
  i18n: II18nMessages;
  quote: IQuoteState;
  renderDefinition(name: string): any;
  actions: {
    quote: IQuoteActions;
  };

  iobsp: IIOBSP;
  ias: IIAS;
}

class IOBSPForm extends React.Component<IIOBSPFormProps, any> {
  public state = {
    declines: [],
    referrals: [],
    errors: [],
  };

  public componentWillReceiveProps(nextProps) {
    const { iobsp, dispatch, change} = this.props;

    if (nextProps.hasOwnProperty('iobsp')) {
      this.resetAnswers(nextProps.iobsp);
    }

    // --------------------------------------------------------------------------------------
    // iobsp.activity._changed is used to make the CheckboxGroup validation work always
    // --------------------------------------------------------------------------------------
    if (iobsp && (iobsp.activity.intermediary !== nextProps.iobsp.activity.intermediary
      || iobsp.activity.broker !== nextProps.iobsp.activity.broker
      || iobsp.activity.agent !== nextProps.iobsp.activity.agent)) {
      const applyDiscount = nextProps.iobsp.activity.intermediary
        && ! nextProps.iobsp.activity.broker
        && ! nextProps.iobsp.activity.agent;
      dispatch(change('iobsp.activity.discount', applyDiscount ? 'true' : 'false'));
      dispatch(change('iobsp.activity._changed', ! iobsp.activity._changed));
    }

    const { quote } = this.props;
    const referralsAndDeclines = getReferralsAndDeclines(quote);
    if (this.state.declines !== referralsAndDeclines.declines) {
      this.setState({ declines: referralsAndDeclines.declines });
    }
    if (this.state.errors !== referralsAndDeclines.errors) {
      this.setState({ errors: referralsAndDeclines.errors });
    }
  }

  private resetAnswers(nextIobsp: IIOBSP) {
    const {dispatch, change, iobsp} = this.props;

    if (!iobsp) {
      return;
    }

    if (this.intermediaryOnly(nextIobsp) && nextIobsp.declaration[1] !== '') {
      dispatch(change('iobsp.declaration[1]', ''));
      dispatch(change('iobsp.declaration[2]', '0'));
      dispatch(change('iobsp.declaration[3]', ''));
    }

    if (!hasSameValue(nextIobsp.declaration, iobsp.declaration, '1') && nextIobsp.declaration[1] !== 'true') {
      dispatch(change('iobsp.declaration[2]', '0'));
      dispatch(change('iobsp.declaration[3]', ''));
    }

    if (!iobsp.primary) {
      if (!hasSameValue(nextIobsp.declaration, iobsp.declaration, '4') && nextIobsp.declaration[4] !== 'false') {
        dispatch(change('iobsp.declaration[5]', ''));
      }
    }
  }

  private intermediaryOnly(iobsp: IIOBSP) {
    if (!iobsp) {
      return false;
    }
    return iobsp.activity.intermediary && !iobsp.activity.broker && !iobsp.activity.agent;
  }

  private getQuestions() {
    const { iobsp } = this.props;
    const questions = [];

    const allQuestions = {
      1: {
        field: 'iobsp.declaration[1]',
        question: 'Avez-vous des mandataires que vous souhaitez assurer dans votre contrat ?',
      },
      2: {
        field: 'iobsp.declaration[2]',
        question: 'Combien ?',
        fieldType: 'number',
      },
      3: {
        field: 'iobsp.declaration[3]',
        question: 'Compte tenu du nombre d\'assurés, souhaitez vous souscrire un relèvement du montant de ' +
          'la garantie RC Professionnelle par période d\'assurance ?',
      },
      // secondary
      a1: {
        field: 'iobsp.declaration[1]',
        question: 'Avez-vous des mandataires personnes physiques que vous souhaitez assurer dans votre contrat ?',
      },
      4: {
        field: 'iobsp.declaration[4]',
        question: 'Encaissez vous les fonds de vos clients ?',
      },
      5: {
        field: 'iobsp.declaration[5]',
        question: 'Souhaitez-vous souscrire tout de même une Garantie Financière sans encaissement ' +
          'de fonds (montant légal de 115 000 euros) ?',
      },
    };

    if (iobsp && iobsp.primary) {
      if (! this.intermediaryOnly(iobsp)) {
        questions.push(allQuestions[1]);

        if (iobsp && iobsp.declaration[1] === 'true') {
          questions.push(allQuestions[2]);
          questions.push(allQuestions[3]);
        }
      }
    } else {
      if (! this.intermediaryOnly(iobsp)) {
        questions.push(allQuestions.a1);
        if (iobsp && iobsp.declaration[1] === 'true') {
          questions.push(allQuestions[2]);
          questions.push(allQuestions[3]);
        }
      }
    }

    questions.push(allQuestions[4]);

    if (iobsp && iobsp.declaration[4] === 'false') {
      questions.push(allQuestions[5]);
    }

    return questions;
  }

  private backToLink() {
    const { ias } = this.props;

    if (ias && ias.included) {
      return '/ias';
    }

    return '/';
  }

  private declinesOrReferrals() {
    const {declines, referrals, errors} = this.state;

    if (!declines.length && !referrals.length && !errors.length) {
      return null;
    }

    return (
      <div>
        <DeclinesAndReferrals
          declines={declines}
          referrals={referrals}
          errors={errors}
          quoteReference={this.props.quote.application.quoteReference}
          renewal={this.props.renewal}
        />
        {(! declines.length && ! referrals.length && errors.length) && (
          <NavigationBar
            hasBackButton={true}
            backTo={this.backToLink()}
            hasNextButton={false}
          />
        )}
      </div>
    );
  }

  private extraValidation = () => {
    return this.validateQuestion4();
  }

  private validateQuestion4 = () => {
    const { iobsp } = this.props;
    if (!iobsp) {
      return true;
    }
    return iobsp.declaration[4] !== 'true';
  }

  public render() {
    const { handleSubmit, iobsp, renderDefinition } = this.props;
    const questions = this.getQuestions();

    const declinesOrReferrals = this.declinesOrReferrals();
    if (declinesOrReferrals) {
      return declinesOrReferrals;
    }

    const isValid = this.extraValidation();

    return (
      <Form horizontal={true}
            onSubmit={handleSubmit}
            autoComplete="off">
        <div className="steps-container">
          <h3>
            {iobsp && iobsp.primary ? 'Votre activité principale' : 'Votre activité Secondaire'}
            : Intermédiation en opérations de banque et service de paiement (IOBSP)
          </h3>

          <FormGroup controlId={'iobsp.activity'}>

            <Col sm={2}>
              {renderDefinition('iobsp')}
              Votre Statut
            </Col>

            <Col sm={8}>
              <div className={'radio-group'}>
                <div className="">
                  <CheckboxGroup
                    name={'ias.activity-xyz'}
                    values={{iobsp}}
                    fields={[
                      {
                        name: 'iobsp.activity.broker',
                        label: 'Courtier en opérations de banque et services de paiement',
                      },
                      {
                        name: 'iobsp.activity.agent',
                        label: 'Mandataire de banque',
                      },
                      {
                        name: 'iobsp.activity.intermediary',
                        label: 'Mandataire d\'intermédiaire en opérations de banque',
                      },
                    ]}
                    {...this.props}
                  />
                </div>
              </div>
            </Col>
          </FormGroup>
          <hr/>

          <strong>Informations complémentaires sur votre activité</strong>

          <DeclarationQuestions renderDefinition={renderDefinition} questions={questions}/>

          {! this.validateQuestion4() && (
            <div className="alert alert-danger">
              <p>
                Nous ne sommes pas en mesure de délivrer une garantie financière avec encaissement de fonds
              </p>
            </div>
          )}

          <NavigationBar
            hasBackButton={true}
            backTo={this.backToLink()}
            nextButtonDisabled={!isValid}
            {...this.props}
          />
        </div>
      </Form>
    );
  }
}

let selector;

export default flow([
  reduxForm({
    form: 'quote',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  }),
  (component) => {
    selector = formValueSelector('quote');
    return component;
  },
  connect(
    (state: IStore) => {
      const startDate = selector(state, 'startDate');
      const quotationTypes = selector(state, 'quotationTypes');
      const iobsp = selector(state, 'iobsp');
      const ias = selector(state, 'ias');

      return {
        startDate,
        quotationTypes,
        auth: state.auth,
        iobsp,
        ias,
      };
    },
    (dispatch) => ({
      actions: {
        quote: bindActionCreators({ ...quoteActions }, dispatch),
      },
    }),
  ),
])(IOBSPForm);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/app/scenes/Quote/scenes/IOBSP/index.tsx